import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IManualMatchingStatus, IManualMatchingStatusFetchParams } from './manual-cta-result-matching-type.interface';
import { ManualMatchingStatusService } from './manual-cta-result-matching-type.service';

interface IManualMatchingStatusListState {
  error: string | null;
  loading: boolean;
  statuses: Map<string, IManualMatchingStatus> | null;
}

const initialState: IManualMatchingStatusListState = {
  error: null,
  loading: false,
  statuses: null,
};

@Injectable({
  providedIn: 'root',
})
export class ManualMatchingStatusListStore extends ComponentStore<IManualMatchingStatusListState> {
  public readonly statuses$ = this.selectSignal((s) => s.statuses);
  public readonly loading$ = this.selectSignal((s) => s.loading);
  public readonly error$ = this.selectSignal((s) => s.error);

  public readonly setSingleStatus = this.updater(
    (state, payload: { ctaTemplateId: string; status: IManualMatchingStatus }) => {
      const newMap = new Map(state.statuses ?? []);

      newMap.set(payload.ctaTemplateId, payload.status);

      return { ...state, statuses: newMap };
    },
  );

  /** Resets to an empty settled map (no overrides). Call when a fetch is intentionally skipped. */
  public readonly clearStatuses = this.updater((state) => ({
    ...state,
    error: null,
    loading: false,
    statuses: new Map(),
  }));

  public readonly loadStatuses = this.effect((params$: Observable<IManualMatchingStatusFetchParams>) =>
    params$.pipe(
      tap(() => this.patchState({ error: null, loading: true, statuses: null })),
      switchMap((params) => {
        const httpParams = new HttpParams()
          .set('campaign_id', params.campaignId)
          .set('creator_id', params.creatorId)
          .set('social_content_id', params.socialContentId)
          .set('cta_template_id__in', params.ctaTemplateIds.join(','))
          .set('limit', 1000);
        const source$ = params.campaignExportId
          ? this.service.getManualMatchingStatusesExport(params.campaignExportId, httpParams)
          : this.service.getManualMatchingStatuses(httpParams);

        return source$.pipe(
          tap((response: IApiResponse<IManualMatchingStatus[]>) => {
            this.patchState({
              error: null,
              loading: false,
              statuses: new Map(response.data?.map((s) => [s.ctaTemplateId, s]) ?? []),
            });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to load manual matching statuses', loading: false, statuses: new Map() });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  constructor(private readonly service: ManualMatchingStatusService) {
    super(initialState);
  }
}
